import { useEffect, useState } from "react";
import { ShieldAlert } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { BreakGlassModal } from "@/components/break-glass/break-glass-modal";
import type { PlacDeniedDetail } from "@/lib/break-glass";

interface BreakGlassDeniedDialogProps {
  detail: PlacDeniedDetail | null;
  onOpenChange: (open: boolean) => void;
  onActivate: (reason: string) => void;
}

export function BreakGlassDeniedDialog({
  detail,
  onOpenChange,
  onActivate,
}: BreakGlassDeniedDialogProps) {
  const [showJustification, setShowJustification] = useState(false);

  useEffect(() => {
    if (!detail) {
      setShowJustification(false);
    }
  }, [detail]);

  return (
    <>
      <Dialog open={!!detail && !showJustification} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300">
                <ShieldAlert className="h-5 w-5" />
              </div>
              <div>
                <DialogTitle>Patient access restricted</DialogTitle>
                <DialogDescription className="mt-1">
                  This record is outside your assigned patient panel.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {detail?.message ||
              "You do not have permission to access this patient record under your current access policy."}
          </p>
          <div className="flex flex-col sm:flex-row gap-2 pt-2">
            <button
              type="button"
              className="flex-1 rounded-lg border border-border px-4 py-2 text-sm font-semibold hover:bg-muted transition-colors"
              onClick={() => onOpenChange(false)}
            >
              Go back
            </button>
            <button
              type="button"
              className="flex-1 rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700 transition-colors"
              onClick={() => setShowJustification(true)}
            >
              Emergency access…
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <BreakGlassModal
        open={showJustification}
        onOpenChange={(open) => {
          setShowJustification(open);
          if (!open) onOpenChange(false);
        }}
        onConfirm={onActivate}
        title="Justify emergency access"
        description="Document why you need immediate access to this patient. After activation, retry your action - emergency headers will be sent automatically."
      />
    </>
  );
}
